import React from "react";
import { Form } from "@components";
import { Wrapper } from "../wrapper";
import { Button, IconButton, Grid, Typography, Box } from "@material-ui/core";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTrash } from "@fortawesome/free-solid-svg-icons";
import {
  CheckCircle as CheckedIcon,
  RadioButtonUnchecked as UncheckedIcon,
} from "@material-ui/icons";
import { Table } from "../../../../../../components/table";

type DriveAccess = {
  sharedDrive: string;
  readOnly: boolean;
  readWrite: boolean;
  readWriteDelete: boolean;
};

export type SharedDriveFormValues = {
  driveAccess: DriveAccess[];
};

type Key = keyof SharedDriveFormValues;

type Props = {
  onCancel: () => void;
  onSubmit: (values: SharedDriveFormValues) => void;
  open: boolean;
  values: Partial<SharedDriveFormValues>;
  readonly?: boolean;
};

export const driveAccessInitialValue: DriveAccess = {
  sharedDrive: "",
  readOnly: false,
  readWrite: false,
  readWriteDelete: false,
};

export const SharedDriveForm: React.FC<Props> = ({
  onCancel,
  onSubmit,
  open,
  values,
  readonly,
}) => {
  const form = Form.useManager<SharedDriveFormValues>({
    driveAccess: [],
    ...values,
  });
  const addSharedDriveForm = Form.useManager<DriveAccess>({
    sharedDrive: "",
    readOnly: false,
    readWrite: false,
    readWriteDelete: false,
  });

  function onAddSharedDriveFormValueChange(
    name: keyof DriveAccess,
    value: DriveAccess[typeof name]
  ): void {
    addSharedDriveForm.set(Form.target.VALUE, [name, value]);
    validateAddSharedDriveFormValue(name, value).then(([name, error]) => {
      addSharedDriveForm.set(Form.target.ERROR, [name, error]);
    });
  }

  function validateAddSharedDriveFormValue(
    name: keyof DriveAccess,
    value: DriveAccess[typeof name]
  ): Promise<[keyof DriveAccess, Error?]> {
    switch (name) {
      case "readOnly":
      case "readWrite":
      case "readWriteDelete": {
        const values = Object.assign(
          {
            readOnly: addSharedDriveForm.values.readOnly,
            readWrite: addSharedDriveForm.values.readWrite,
            readWriteDelete: addSharedDriveForm.values.readWriteDelete,
          },
          {
            [name]: value,
          }
        );

        if (Object.values(values).every((value) => value === false)) {
          return Promise.resolve([
            "readOnly",
            Form.Validators.hasValue(undefined),
          ]);
        } else {
          return Promise.resolve(["readOnly"]);
        }
      }
      case "sharedDrive":
        return Promise.resolve([
          name as keyof DriveAccess,
          Form.Validators.hasValue(value),
        ]);
      default:
        return Promise.resolve([name as keyof DriveAccess]);
    }
  }

  function onAddSharedDrive() {
    Promise.all(
      Object.entries(addSharedDriveForm.values).map(([name, value]) =>
        validateAddSharedDriveFormValue(name as keyof DriveAccess, value)
      )
    ).then((validations) => {
      const valid = validations.reduce((valid, [name, error]) => {
        return valid && !error;
      }, true);

      if (valid) {
        const name = "driveAccess";
        const value = form.values.driveAccess.concat(addSharedDriveForm.values);

        form.set(Form.target.VALUE, [name, value]);
        validateFormValue(name, value).then(([name, error]) => {
          form.set(Form.target.ERROR, [name, error]);
        });
        addSharedDriveForm.reset();
      } else {
        validations.forEach(([name, error]) => {
          addSharedDriveForm.set(Form.target.ERROR, [name, error]);
        });
      }
    });
  }

  function onRemoveSharedDrive(index: number): void {
    onFormValueChange(
      "driveAccess",
      form.values.driveAccess.filter((driveAccess, i) => i !== index)
    );
  }

  function onFormValueChange(
    name: Key,
    value: SharedDriveFormValues[typeof name]
  ): void {
    form.set(Form.target.VALUE, [name, value]);
    validateFormValue(name, value).then(([name, error]) => {
      form.set(Form.target.ERROR, [name, error]);
    });
  }

  function validateFormValue(
    name: Key,
    value: SharedDriveFormValues[typeof name]
  ): Promise<[Key, Error?]> {
    switch (name) {
      case "driveAccess":
        return Promise.resolve([name as Key, Form.Validators.hasValue(value)]);
      default:
        return Promise.resolve([name as Key]);
    }
  }

  function onFormSubmit(): void {
    Promise.all(
      Object.entries(form.values).map(([name, value]) =>
        validateFormValue(name as Key, value)
      )
    ).then((validations) => {
      const valid = validations.reduce((valid, [name, error]) => {
        return valid && !error;
      }, true);

      if (valid) {
        onSubmit(form.values);
      } else {
        validations.forEach(([name, error]) => {
          form.set(Form.target.ERROR, [name, error]);
        });
      }
    });
  }

  return (
    <Wrapper
      onCancel={() => {
        onCancel();
        if (values) {
          form.set(Form.target.VALUES, values);
        } else {
          form.reset();
        }
      }}
      onSubmit={onFormSubmit}
      open={open}
      title="Shared Drive Access"
      readonly={readonly}
      size="md"
    >
      <Form>
        <Form.Title>1 - Shared Drive</Form.Title>
        <Grid item xs={12}>
          <Box border={1} borderColor="grey.500">
            <Box p={2} bgcolor="#8A92A5" color="white">
              <Typography variant="subtitle2" color="inherit">
                Input Drive
              </Typography>
            </Box>
            <Box p={2}>
              <Grid container spacing={2}>
                <Form.Textbox
                  label="eg.\\LE1PF-0001\XTEAM"
                  name="sharedDrive"
                  onChange={onAddSharedDriveFormValueChange}
                  size={6}
                  required
                  value={addSharedDriveForm.values.sharedDrive}
                />
                <Grid container item xs={6} alignItems="center">
                  <Box pt="22px">
                    <Form.Error error={addSharedDriveForm.errors.sharedDrive} />
                  </Box>
                </Grid>
                <Form.Radiobutton
                  label="Read Only"
                  name="readOnly"
                  onChange={onAddSharedDriveFormValueChange}
                  size={3}
                  value={addSharedDriveForm.values.readOnly}
                />
                <Form.Radiobutton
                  label="Read/Write"
                  name="readWrite"
                  onChange={onAddSharedDriveFormValueChange}
                  size={3}
                  value={addSharedDriveForm.values.readWrite}
                />
                <Form.Radiobutton
                  label="Read/Write/Delete"
                  name="readWriteDelete"
                  onChange={onAddSharedDriveFormValueChange}
                  size={3}
                  value={addSharedDriveForm.values.readWriteDelete}
                />
                <Grid container item xs={3} alignItems="center">
                  <Form.Error error={addSharedDriveForm.errors.readOnly} />
                </Grid>
              </Grid>
            </Box>
          </Box>
        </Grid>
        <Grid item xs={12}>
          <Button variant="outlined" onClick={onAddSharedDrive}>
            + Add
          </Button>
        </Grid>
        <Grid item xs={12}>
          <Typography variant="subtitle2">List</Typography>
        </Grid>
        <Grid item xs={12}>
          <Table
            columns={[
              { label: "Shared Drive" },
              { label: "Read Only" },
              { label: "Read/Write" },
              { label: "Read/Write/Delete" },
              { label: "" },
            ]}
            rowHeight={43}
          >
            {form.values.driveAccess.length ? (
              form.values.driveAccess.map((access, index) => (
                <Table.Row key={index}>
                  <Table.Cell>{access.sharedDrive}</Table.Cell>
                  <Table.Cell>
                    {access.readOnly ? (
                      <Box display="flex" color="success.main">
                        <CheckedIcon />
                      </Box>
                    ) : (
                      <Box display="flex" color="grey.200">
                        <UncheckedIcon />
                      </Box>
                    )}
                  </Table.Cell>
                  <Table.Cell>
                    {access.readWrite ? (
                      <Box display="flex" color="success.main">
                        <CheckedIcon />
                      </Box>
                    ) : (
                      <Box display="flex" color="grey.200">
                        <UncheckedIcon />
                      </Box>
                    )}
                  </Table.Cell>
                  <Table.Cell>
                    {access.readWriteDelete ? (
                      <Box display="flex" color="success.main">
                        <CheckedIcon />
                      </Box>
                    ) : (
                      <Box display="flex" color="grey.200">
                        <UncheckedIcon />
                      </Box>
                    )}
                  </Table.Cell>
                  <Table.Cell>
                    <IconButton
                      size="small"
                      onClick={() => {
                        onRemoveSharedDrive(index);
                      }}
                    >
                      {/* <DeleteIcon fontSize="small" /> */}
                      <FontAwesomeIcon icon={faTrash} />
                    </IconButton>
                  </Table.Cell>
                </Table.Row>
              ))
            ) : form.errors.driveAccess ? (
              <Box
                height="43px"
                display="flex"
                alignItems="center"
                justifyContent="center"
              >
                <Form.Error error={form.errors.driveAccess} />
              </Box>
            ) : (
              <Box
                height="43px"
                display="flex"
                alignItems="center"
                justifyContent="center"
                color="text.disabled"
              >
                <Typography variant="caption">No information added</Typography>
              </Box>
            )}
          </Table>
        </Grid>
      </Form>
    </Wrapper>
  );
};
